// ISO 3166-1 alpha-2 country codes used by the billing/fiscal flow.
// Names are in Spanish (admin and UI is currently es-ES only).
//
// `EU_COUNTRIES` is kept in sync with the same set the backend uses in
// `backend/utils/vatValidation.js`. Order: EU first (sorted by name), then
// common non-EU jurisdictions, then everything else.

export const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR',
  'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL',
  'PT', 'RO', 'SE', 'SI', 'SK'
]

export const COUNTRIES = [
  // EU (sorted by Spanish name)
  { code: 'DE', name: 'Alemania', eu: true },
  { code: 'AT', name: 'Austria', eu: true },
  { code: 'BE', name: 'Bélgica', eu: true },
  { code: 'BG', name: 'Bulgaria', eu: true },
  { code: 'CY', name: 'Chipre', eu: true },
  { code: 'HR', name: 'Croacia', eu: true },
  { code: 'DK', name: 'Dinamarca', eu: true },
  { code: 'SK', name: 'Eslovaquia', eu: true },
  { code: 'SI', name: 'Eslovenia', eu: true },
  { code: 'ES', name: 'España', eu: true },
  { code: 'EE', name: 'Estonia', eu: true },
  { code: 'FI', name: 'Finlandia', eu: true },
  { code: 'FR', name: 'Francia', eu: true },
  { code: 'GR', name: 'Grecia', eu: true },
  { code: 'HU', name: 'Hungría', eu: true },
  { code: 'IE', name: 'Irlanda', eu: true },
  { code: 'IT', name: 'Italia', eu: true },
  { code: 'LV', name: 'Letonia', eu: true },
  { code: 'LT', name: 'Lituania', eu: true },
  { code: 'LU', name: 'Luxemburgo', eu: true },
  { code: 'MT', name: 'Malta', eu: true },
  { code: 'NL', name: 'Países Bajos', eu: true },
  { code: 'PL', name: 'Polonia', eu: true },
  { code: 'PT', name: 'Portugal', eu: true },
  { code: 'CZ', name: 'República Checa', eu: true },
  { code: 'RO', name: 'Rumanía', eu: true },
  { code: 'SE', name: 'Suecia', eu: true },
  // Common non-EU
  { code: 'AR', name: 'Argentina', eu: false },
  { code: 'AU', name: 'Australia', eu: false },
  { code: 'BR', name: 'Brasil', eu: false },
  { code: 'CA', name: 'Canadá', eu: false },
  { code: 'CL', name: 'Chile', eu: false },
  { code: 'CO', name: 'Colombia', eu: false },
  { code: 'GB', name: 'Reino Unido', eu: false },
  { code: 'MX', name: 'México', eu: false },
  { code: 'CH', name: 'Suiza', eu: false },
  { code: 'NO', name: 'Noruega', eu: false },
  { code: 'PE', name: 'Perú', eu: false },
  { code: 'US', name: 'Estados Unidos', eu: false },
  { code: 'UY', name: 'Uruguay', eu: false }
]

// Mirror of backend `inferVatTreatment` so the UI can preview the chosen
// treatment as soon as the country changes (no server round-trip).
export const inferVatTreatment = (country) => {
  if (!country) return 'pending_tax_review'
  const c = String(country).toUpperCase()
  if (c === 'ES') return 'es_vat_included_21'
  if (EU_COUNTRIES.includes(c)) return 'eu_b2b_reverse_charge'
  return 'non_eu_b2b_out_of_scope'
}

export const VAT_TREATMENT_LABELS = {
  es_vat_included_21: 'IVA España 21% incluido',
  es_territory_outside_vat: 'Territorio español fuera del IVA (Canarias / Ceuta / Melilla)',
  eu_b2b_reverse_charge: 'B2B intracomunitario (inversión sujeto pasivo)',
  non_eu_b2b_out_of_scope: 'B2B fuera UE (no sujeto al IVA español)',
  pending_tax_review: 'Pendiente de revisión'
}

// Client-side mirror of backend regex set. Validation is best-effort here —
// the backend is the source of truth and will reject malformed values.
const VAT_PATTERNS = {
  AT: /^(AT)?U[0-9]{8}$/,
  BE: /^(BE)?[01][0-9]{9}$/,
  BG: /^(BG)?[0-9]{9,10}$/,
  CY: /^(CY)?[0-9]{8}[A-Z]$/,
  CZ: /^(CZ)?[0-9]{8,10}$/,
  DE: /^(DE)?[0-9]{9}$/,
  DK: /^(DK)?[0-9]{8}$/,
  EE: /^(EE)?[0-9]{9}$/,
  ES: /^(ES)?([A-Z][0-9]{8}|[A-Z][0-9]{7}[A-Z]|[0-9]{8}[A-Z])$/,
  FI: /^(FI)?[0-9]{8}$/,
  FR: /^(FR)?[A-Z0-9]{2}[0-9]{9}$/,
  GR: /^(EL|GR)?[0-9]{9}$/,
  HR: /^(HR)?[0-9]{11}$/,
  HU: /^(HU)?[0-9]{8}$/,
  IE: /^(IE)?([0-9]{7}[A-Z]{1,2}|[0-9][A-Z][0-9]{5}[A-Z])$/,
  IT: /^(IT)?[0-9]{11}$/,
  LT: /^(LT)?([0-9]{9}|[0-9]{12})$/,
  LU: /^(LU)?[0-9]{8}$/,
  LV: /^(LV)?[0-9]{11}$/,
  MT: /^(MT)?[0-9]{8}$/,
  NL: /^(NL)?[0-9]{9}B[0-9]{2}$/,
  PL: /^(PL)?[0-9]{10}$/,
  PT: /^(PT)?[0-9]{9}$/,
  RO: /^(RO)?[0-9]{2,10}$/,
  SE: /^(SE)?[0-9]{12}$/,
  SI: /^(SI)?[0-9]{8}$/,
  SK: /^(SK)?[0-9]{10}$/
}

export const validateTaxIdLocally = ({ taxId, country }) => {
  if (!taxId || !country) return { valid: false, reason: 'missing' }
  const normalized = String(taxId).replace(/[\s.\-]/g, '').toUpperCase()
  const c = String(country).toUpperCase()
  const pattern = VAT_PATTERNS[c]
  if (!pattern) return { valid: true, reason: 'no_pattern' }
  return { valid: pattern.test(normalized), normalized }
}
